import { driver } from "driver.js";
import type { TFunction } from "i18next";
import type { GuideStep } from "./types";

const isVisible = (sel?: string) => {
  if (!sel) return true;
  const el = document.querySelector(sel);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

/**
 * Centra el target antes de reposicionar el popover — pero SOLO si hace falta.
 * `scrollIntoView` sube por todos los ancestros scrolleables, y en un iframe eso
 * incluye el documento padre: la guía del tour embebido en la landing scrolleaba
 * la landing entera. Embebido (el viewer no scrollea) no se toca nada; suelto,
 * sólo si el elemento quedó fuera de la ventana.
 */
export function scrollTargetIntoView(el: Element) {
  if (window.parent !== window) return;
  const r = el.getBoundingClientRect();
  const inView =
    r.top >= 0 &&
    r.left >= 0 &&
    r.bottom <= window.innerHeight &&
    r.right <= window.innerWidth;
  if (inView) return;
  el.scrollIntoView({ block: "center", inline: "nearest" });
}

/** Run a standalone driver.js tour over a fixed set of steps — used by the
 *  per-modal help button, which guides an already-open modal (no main tour to
 *  resume). Mirrors <DashboardGuide>'s options + the modal-dismiss guard. */
export function runModalGuide(steps: GuideStep[], t: TFunction) {
  const visible = steps.filter((s) => isVisible(s.target));
  if (!visible.length) return;

  // Swallow pointerdowns outside the popover so a stray click can't dismiss the
  // modal mid-tour (capture beats Radix's bubble-phase dismiss listener).
  const guard = (e: Event) => {
    const el = e.target as Element | null;
    if (el?.closest?.(".driver-popover")) return;
    e.stopImmediatePropagation();
  };
  document.addEventListener("pointerdown", guard, true);

  const d = driver({
    showProgress: true,
    popoverClass: "andar-guide",
    nextBtnText: t("guide_next", "Siguiente"),
    prevBtnText: t("guide_prev", "Atrás"),
    doneBtnText: t("guide_done", "Listo"),
    onHighlighted: (el?: Element) => {
      if (!el) return;
      scrollTargetIntoView(el);
      requestAnimationFrame(() => d.refresh());
    },
    onDestroyed: () =>
      document.removeEventListener("pointerdown", guard, true),
    steps: visible.map((s) => ({
      element: s.target,
      popover: { title: t(s.titleKey), description: t(s.bodyKey) },
    })),
  });
  d.drive();
}
